(() => {
  if (window.HobbyTigerDataset?.schema === 2) return;
  const config = window.HobbyTigerDatasetConfig || {};
  const requests = new Map(), datasets = new Map();
  const digest = async text => Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256',new TextEncoder().encode(text))),b=>b.toString(16).padStart(2,'0')).join('');
  const validProduct = p => p && /^gid:\/\/shopify\/Product\/\d+$/.test(p.id) && typeof p.handle==='string' && typeof p.title==='string'
    && Array.isArray(p.collections) && p.collections.every(h=>typeof h==='string') && p.fields && Object.values(p.fields).every(v=>Array.isArray(v)&&v.every(s=>typeof s==='string'))
    && Number.isFinite(p.price) && Number.isFinite(p.createdTs) && typeof p.available==='boolean';
  const signal = (type,detail) => window.dispatchEvent(new CustomEvent(`hobbytiger:dataset:${type}`,{detail}));
  function urlFor(value) {
    const url=new URL(value,location.href), root=new URL(config.manifestUrl,location.href);
    if(url.protocol!=='https:' || (url.origin!==root.origin && url.hostname!=='cdn.shopify.com')) throw new Error('Unexpected dataset CDN origin');
    return url.href;
  }
  function request(url,expectedHash) {
    const key=`${url}:${expectedHash||'root'}`;
    if(!requests.has(key)) requests.set(key,(async()=>{
      const controller=new AbortController(), timer=setTimeout(()=>controller.abort(),config.timeoutMs || 8000);
      try {
        const response=await fetch(urlFor(url),{signal:controller.signal,credentials:'omit',cache:expectedHash?'default':'no-cache'});
        if(!response.ok)throw new Error(`Dataset HTTP ${response.status}`);
        const content=await response.text();
        if(expectedHash && await digest(content)!==expectedHash)throw new Error('Dataset checksum mismatch');
        const data=JSON.parse(content);
        if(data?.schema!==2)throw new Error('Unsupported dataset version');
        return data;
      } finally { clearTimeout(timer); }
    })().catch(error=>{requests.delete(key);throw error;}));
    return requests.get(key);
  }
  async function loadV2(scope) {
    const root=await request(config.manifestUrl);
    const bucket=(await digest(scope)).slice(0,2), route=root.buckets?.[bucket];
    if(!route?.url||!/^[a-f0-9]{64}$/.test(route.sha256))throw new Error('Missing V2 route');
    const index=await request(route.url,route.sha256), descriptor=index.scopes?.[scope];
    if(!descriptor || !Number.isInteger(descriptor.count) || descriptor.count<0 || !Array.isArray(descriptor.parts))throw new Error('Missing V2 scope');
    if(descriptor.expiresAt && (!Number.isFinite(Date.parse(descriptor.expiresAt)) || Date.parse(descriptor.expiresAt)<=Date.now()))throw new Error('Homepage selection expired; refresh dataset');
    const products=[],ids=new Set();
    // A collection is complete before filters initialize. These parts bound transfer/file
    // size, NOT server pagination. At most three requests in flight, preserving order.
    for(let offset=0;offset<descriptor.parts.length;offset+=3) {
      const pages=await Promise.all(descriptor.parts.slice(offset,offset+3).map(async part=>{
        if(!/^[a-f0-9]{64}$/.test(part.sha256))throw new Error('Invalid V2 part hash');
        const page=await request(part.url,part.sha256);
        // Small collections share a bounded pack. Never merge neighbouring scopes.
        const products=page.format==='scope-pack-v1'
          ? (Object.prototype.hasOwnProperty.call(page.scopes || {},scope) ? page.scopes[scope] : null)
          : (page.scope===scope ? page.products : null);
        if(!Array.isArray(products))throw new Error('Wrong V2 scope');return products;
      }));
      for(const page of pages) for(const product of page) {
        if(!validProduct(product)||ids.has(product.id))throw new Error('Invalid or duplicate V2 product');
        ids.add(product.id);products.push(product);
      }
    }
    if(products.length!==descriptor.count)throw new Error('Incomplete V2 scope');
    const collections=Object.create(null);
    if(scope.startsWith('collection:')) {
      const counts=Object.create(null);counts.subtitles=Object.create(null);counts.language=Object.create(null);
      for(const p of products) for(const [key,values] of Object.entries(p.fields)) {
        const bucket=counts[key] ||= Object.create(null);
        for(const value of new Set(values))if(value.trim())bucket[value]=(bucket[value] || 0)+1;
      }
      collections[scope.slice('collection:'.length)]=counts;
    }
    return {schema:2,scope,generatedAt:root.generatedAt,products,facetCounts:{collections}};
  }
  function load(scope,fallbackScope) {
    if(!datasets.has(scope)) datasets.set(scope,(async()=>{
      try {const result=await loadV2(scope);signal('loaded',{version:2,scope,count:result.products.length});return result;}
      catch(error) {
        // A missing/expired home selection can use a complete V2 collection.
        if(fallbackScope) {
          const result=await loadV2(fallbackScope);
          signal('loaded',{version:2,scope:fallbackScope,count:result.products.length});return result;
        }
        throw error;
      }
    })().catch(error=>{
      datasets.delete(scope);requests.clear();
      signal('error',{scope,reason:error.message});throw error;
    }));
    return datasets.get(scope);
  }
  function collection(section) {
    const taxonomy=section?.dataset?.htTaxonomy || new URLSearchParams(location.search).get('taxonomy');
    // Taxonomy can span multiple collections; let the existing engine filter all.
    return load(`collection:${taxonomy?'all':section?.dataset?.htCollectionHandle || 'all'}`);
  }
  // Keep this eligibility rule identical to dataset-refresh-admin/dataset-prizes.js.
  function isPrizeProduct(product) {
    const normalize = value => String(value || '').normalize('NFKC').trim().toLowerCase()
      .replace(/[^a-z0-9\u3040-\u30ff\u3400-\u9fff]+/g, '-').replace(/^-|-$/g, '');
    const values = value => Array.isArray(value) ? value : typeof value === 'string' ? [value] : [];
    const marked = value => {
      const key = normalize(value);
      return /(^|-)prizes?(-|$)/.test(key) || /プライズ|一番くじ/.test(key)
        || key === 'ichiban-kuji' || key.startsWith('ichiban-kuji-')
        || key === 'banpresto-crane-game' || key.startsWith('banpresto-crane-game-');
    };
    return [product?.productType, ...values(product?.fields?.type),
      ...values(product?.fields?.productLine), ...values(product?.fields?.product_line),
      ...values(product?.collections)].some(marked);
  }
  function home(section,type) {
    const spec={type,collection:type==='new-arrivals' ? section.dataset.htCollectionFilter || '' : '',
      figuresOnly:type==='new-arrivals' && section.dataset.htFiguresOnly==='true',
      availableOnly:type==='new-arrivals' && section.dataset.htAvailableOnly==='true',
      limit:Math.max(1,Math.min(24,Number(section.dataset.htLimit) || (type==='running-pre-orders'?12:8)))};
    if(type==='new-arrivals' && section.dataset.htExcludePrizes==='true') spec.excludePrizes=true;
    const fallbackScope=type==='new-arrivals'?`collection:${spec.collection || 'all'}`:type==='running-pre-orders'?'collection:pre-order':'collection:all';
    return load(`home:${JSON.stringify(spec)}`,fallbackScope);
  }
  window.HobbyTigerDataset={schema:2,load,collection,home,isPrizeProduct};
  window.hobbyTigerDatasetLoaded?.(window.HobbyTigerDataset);
})();
